<template>
  <BasePageContainer class="notifications">
    <div>
      <transition-group
        name="notification-card"
        @after-leave="scrollToNextNotification">
        <template v-for="(notification, index) in notifications">
          <notification-card
            :ref="notification.id"
            :key="notification.id"
            :notification="notification"
            :lock-goal-option="lockGoalOption"
            @check-read="onCheckRead($event, index)">
            <notification-card-for-activity-result
              v-if="notification.type === 'activity_result'"
              :notification="notification"
              @click-notification="onClickNotification(notification)" />
            <notification-card-for-comment
              v-else-if="notification.type === 'comment'"
              :notification="notification"
              @click-notification="onClickNotification(notification)" />
            <notification-card-for-event-comment
              v-else-if="notification.type === 'event_comment'"
              :event="eventById[notification.eventComment.event]"
              :notification="notification"
              @click-notification="onClickNotification(notification)" />
            <NotificationCardForMandalaEvaluation
              v-else-if="notification.type === 'mandala_goal_self_evaluation'"
              :notification="notification"
              @click-notification="onClickNotification(notification)" />
            <NotificationCardForMandalaEvaluationComment
              v-else-if="notification.type === 'mandala_goal_self_evaluation_comment'"
              :notification="notification"
              @click-notification="onClickNotification(notification)" />
            <template
              v-if="notification.type === 'activity_result'"
              #actions>
              <notification-like-button
                :notification="notification"
                @like="onLike"
                @dislike="onDislike" />
            </template>
            <template
              v-else-if="notification.type === 'event_comment'"
              #actions>
              <notification-card-actions
                class="mr-2"
                :event="eventById[notification.eventComment.event]"
                @attend="attendEvent(eventById[notification.eventComment.event])"
                @absence="absenceEvent(eventById[notification.eventComment.event])"
                @cancel="cancelAttendEvent(eventById[notification.eventComment.event])" />
            </template>
            <template
              v-else-if="notification.type === 'mandala_goal_self_evaluation'"
              #actions>
              <SvgTextButton
                :src="require('@/assets/images/icon_chart.svg')"
                @click="$router.push(`/members/${notification.mandalaGoalSelfEvaluation.user.id}/`)">
                チャート
              </SvgTextButton>
            </template>
          </notification-card>
        </template>
      </transition-group>
      <infinite-loading
        spinner="circles"
        @infinite="infiniteHandler">
        <div slot="no-more" />
        <div slot="no-results" />
      </infinite-loading>
      <div
        v-if="showEmptyText"
        class="d-flex justify-content-center pt-5">
        通知はありません。
      </div>
    </div>
    <div
      v-if="hasNotifications"
      class="notifications__btn-wrapper">
      <button
        class="btn btn-l-blue notifications__btn"
        @click="onClickDeleteNotifications">
        全てを既読
      </button>
    </div>
  </BasePageContainer>
</template>

<script>
import { mapState } from 'vuex'
import NotificationCard from '@/components/view/NotificationCard'
import NotificationCardActions from '@/components/view/NotificationCardActions.vue'
import NotificationCardForActivityResult from '@/components/view/NotificationCardForActivityResult'
import NotificationCardForComment from '@/components/view/NotificationCardForComment'
import NotificationCardForEventComment from '@/components/view/NotificationCardForEventComment'
import NotificationCardForMandalaEvaluation from '@/components/view/NotificationCardForMandalaEvaluation.vue'
import NotificationCardForMandalaEvaluationComment from '@/components/view/NotificationCardForMandalaEvaluationComment.vue'
import NotificationLikeButton from '@/components/view/NotificationLikeButton.vue'
import { format } from 'date-fns'
import InfiniteLoading from 'vue-infinite-loading'
import SvgTextButton from '@/components/parts/SvgTextButton.vue'

export default {
  components: {
    NotificationCard,
    NotificationCardActions,
    NotificationCardForActivityResult,
    NotificationCardForComment,
    NotificationCardForEventComment,
    NotificationCardForMandalaEvaluation,
    NotificationCardForMandalaEvaluationComment,
    NotificationLikeButton,
    SvgTextButton,
    InfiniteLoading,
  },
  data() {
    return {
      notifications: [],
      isLoadingInLocal: false,
      limit: 20,
      offset: 0,
      next: null,
      infiniteComplete: false,
      lockGoalOption: false,
      scrollTargetId: null,
      events: []
    }
  },
  computed: {
    ...mapState('events', ['selectedCategories']),
    hasNotifications() {
      return this.notifications.length > 0
    },
    showEmptyText() {
      // infiniteHandlerの$state.complete()が呼ばれる前は表示しない
      if (!this.infiniteComplete) return false
      // complete()呼ばれた後に通知が0件であれば表示する
      return !this.hasNotifications
    },
    eventById() {
      return this.events.reduce((prev, curr) => {
        return { ...prev, [curr.id]: curr }
      }, {})
    }
  },
  async created() {
    this.$store.commit('startLoading')
    this.lockGoalOption = await this.$axios.get('/tenants/')
      .then((res) => res.data.lockGoalOption)
      .catch(() => this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'}))
    this.$store.commit('finishLoading')
  },
  methods: {
    async infiniteHandler($state) {
      const res = await this.fetchNotifications()
      const newNotifications = [...this.notifications, ...res.data.results]

      const eventIds = newNotifications.map(notification => notification.type === 'event_comment' ? notification.eventComment.event : null).filter(id => id !== null)
      const uniqEventIds = [...new Set(eventIds)]
      this.events = await Promise.all([...uniqEventIds.map(id => this.fetchEvent(id))])
    
      this.notifications = newNotifications
      if (res.data.next) {
        this.next = res.data.next
        this.offset += this.limit
        $state.loaded()
      } else {
        $state.complete()
        this.infiniteComplete = true
      }
    },
    fetchNotifications() {
      return this.$axios.get('/activities/notifications/', {
        params: {
          order: '-created_at',
          limit: this.limit,
          offset: this.offset,
        }
      }).catch(() => {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
    },
    fetchEvent(eventId) {
      return this.$axios.get(`/events/${eventId}/`).then(res => res.data)
    },
    async updateEvent(eventId) {
      const event = await this.fetchEvent(eventId)
      const index = this.events.findIndex(event => event.id === eventId)
      this.events.splice(index, 1, event)
    },
    async attendEvent(event) {
      this.$store.commit('startLoading')
      await this.$axios.post(`/events/${event.id}/attend/`).then(() => {
        this.$store.commit('notify/set', {
          message: `${event.title}に参加します`
        })
        this.updateEvent(event.id)
      }).catch(err => {
        console.error(err)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
      this.$store.commit('finishLoading')
    },
    async absenceEvent(event) {
      this.$store.commit('startLoading')
      await this.$axios.post(`/events/${event.id}/absence/`).then(() => {
        this.$store.commit('notify/set', {
          message: `${event.title}に参加しません`
        })
        this.updateEvent(event.id)
      }).catch(err => {
        console.error(err)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
      this.$store.commit('finishLoading')
    },
    async cancelAttendEvent(event) {
      this.$store.commit('startLoading')
      const isAttend = event.myReply?.isAttend
      await this.$axios.delete(`/events/${event.id}/attend/`).then(() => {
        this.$store.commit('notify/set', {
          message: `${event.title}の${isAttend ? '参加' : '不参加'}をキャンセルしました`
        })
        this.updateEvent(event.id)
      }).catch(err => {
        console.error(err)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
      this.$store.commit('finishLoading')
    },
    async onClickDeleteNotifications() {
      if (!window.confirm('全ての通知を既読にしますか？')) return
      this.$store.commit('startLoading')
      try {
        await this.$axios.delete('/activities/notifications/delete_all/')
        // フロントの通知もリセット
        this.notifications = []
      }
      catch {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      }
      // ここでstoreのhasNotificationsを更新しないと既読にして通知が0になった場合でも通知用バッジが残ったままとなってしまう
      this.$store.commit('setHasNotifications', this.hasNotifications)
      this.$store.commit('finishLoading')
      // 既読押下時に先頭までスクロール
      window.scrollTo(0, 0)
    },
    // 既読はスムーズに行うため、グローバルなローディングはいれない
    async onCheckRead(notificationId, index) {
      if (this.isLoadingInLocal) return
      this.isLoadingInLocal = true

      // 一番下の通知でなければ一つ下のnotificationに, 一番下なら次に一番下になるnotificationを指定, 最後の1件の通知の場合何もしない
      const scrollTargetIndex = this.notifications.length > index + 1 ? index + 1 : index - 1
      if (this.notifications.length > 1) {
        this.scrollTargetId = this.notifications.find((item, idx) => idx === scrollTargetIndex).id
      }

      this.notifications = this.notifications.filter((n) => n.id !== notificationId)

      await this.deleteNotification(notificationId).catch(() => {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
      // ここでstoreのhasNotificationsを更新しないと既読にして通知が0になった場合でも通知用バッジが残ったままとなってしまう
      this.$store.commit('setHasNotifications', this.hasNotifications)

      this.isLoadingInLocal = false
    },
    async onClickNotification(notification) {
      this.$store.commit('startLoading')

      if (notification.type === 'event_comment') {
        const event = this.eventById[notification.eventComment.event]
        if (!event) {
          this.$store.commit('notify/set', {type: 'danger', message: '予定が削除されたため、コメントを表示できません。'})
          this.$store.commit('finishLoading')
          return
        }

        // 予定のコメントが削除された場合、遷移せずに通知を削除
        const latestEvent = await this.fetchEvent(event.id)
        const isExistEventComment = latestEvent.eventComments.some(comment => comment.id === notification.eventComment.id)
        if (!isExistEventComment) {
          // 通知の削除
          this.notifications = this.notifications.filter((n) => n.id !== notification.id)
          this.$store.commit('notify/set', { message: 'このコメントは、コメントしたメンバーによって削除されています。'})
          this.$store.commit('finishLoading')
          return
        }

        const categoryId = event.category.id
        // 表示しようとしている予定が表示選択しているカテゴリにあるかを確認
        if (!this.selectedCategories.includes(categoryId)) {
          if (!window.confirm('あなたが表示選択で設定していないカテゴリーの予定です。このカテゴリーを表示選択に追加し詳細画面に移動しますか？')) {
            this.$store.commit('finishLoading')
            return
          }
          this.$store.commit('events/setSelectedCategories', [...this.selectedCategories, categoryId])
        }
      }

      const params = {}
      // 通知(コメント)
      if (notification.type === 'comment'){
        params.memberId = notification.comment.activityResult.createdBy
        params.goalId = notification.comment.activityResult.goal
        params.activityId = notification.comment.activityResult.id
        params.goalEndDate = format(new Date(notification.comment.activityResult.workAt), 'yyyy-MM-dd')
      }
      // 通知(活動記録)
      else if (notification.type === 'activity_result'){
        params.memberId = notification.activityResult.createdBy.id
        params.goalId = notification.activityResult.goal.id
        params.activityId = notification.activityResult.id
        params.goalEndDate = format(new Date(notification.activityResult.workAt), 'yyyy-MM-dd')
      }
      // 通知(予定)
      else if (notification.type === 'event_comment') {
        params.memberId = this.eventById[notification.eventComment.event].createdBy.id
        params.eventId = notification.eventComment.event
        params.eventCommentId = notification.eventComment.id
      }
      // 自己評価提出
      else if (notification.type === 'mandala_goal_self_evaluation') {
        const { user, yearMonth } = notification.mandalaGoalSelfEvaluation
        params.memberId = user.id
        params.yearMonth = yearMonth
      }
      // 自己評価へのコメント
      else if (notification.type === 'mandala_goal_self_evaluation_comment') {
        const { mandalaGoalSelfEvaluationComment } = notification
        const { user, yearMonth } = mandalaGoalSelfEvaluationComment.mandalaGoalSelfEvaluation
        params.memberId = user.id
        params.yearMonth = yearMonth
        params.commentId = mandalaGoalSelfEvaluationComment.id
      }
      else {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
        this.$store.commit('finishLoading')
        return
      }

      // ユーザーが表示できるかチェック
      const isUserIsValid = await this.validateActivityCreator(params.memberId)
      if (!isUserIsValid) {
        const recordName = notification.type === 'event_comment' ? '予定' : '活動記録' 
        this.$store.commit('notify/set', {type: 'danger', message: `選択した${recordName}は該当のユーザーが利用停止等の理由で閲覧できなくなったため表示できません。`})
        this.$store.commit('finishLoading')
        return
      }

      // 通知の削除
      await this.deleteNotification(notification.id).catch((e) => {
        console.error(e)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })

      if (['comment', 'activity_result'].includes(notification.type)) {
        this.$router.push(`/members/${params.memberId}?goalId=${params.goalId}&activityId=${params.activityId}&goalEndDate=${params.goalEndDate}&isOpenSelectedMember=true`)
      } else if (notification.type === 'event_comment') {
        this.$router.push(`/events/?eventId=${params.eventId}&eventCommentId=${params.eventCommentId}&isOpenSelectedMember=true`)
      } else if (notification.type === 'mandala_goal_self_evaluation') {
        this.$router.push(`/members/${params.memberId}/mandala_evaluation/?yearMonth=${params.yearMonth}&isOpenSelectedMember=true`)
      } else if (notification.type === 'mandala_goal_self_evaluation_comment') {
        this.$router.push(`/members/${params.memberId}/mandala_evaluation/?yearMonth=${params.yearMonth}&scrollTo=${params.commentId}&isOpenSelectedMember=true`)
      }
      this.$store.commit('finishLoading')
    },
    // 通知に紐付く活動の作成者が有効かどうか(利用停止になっていないかorログインユーザーにとって閲覧対象か)を判定
    validateActivityCreator(memberId) {
      return this.$axios.get(`/users/${memberId}/`)
        .then(() => true)
        .catch((e) => {
          console.error(e)
          return false
        })
    },
    fetchGoal(goalId) {
      return this.$axios.get(`/goals/${goalId}/`)
    },
    deleteNotification(notificationId) {
      return this.$axios.delete(`/activities/notifications/${notificationId}/`)
    },
    async goalEndDate(notification, goalId) {
      if (notification.type === 'activity_result') {
        return notification.activityResult.goal.endDate
      }
      else {
        const goal = await this.fetchGoal(goalId)
        return goal.data.endDate
      }
    },
    async onLike(activityId) {
      await this.$axios.post(`/activities/activity_results/${activityId}/like/`).catch(() => {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
    },
    async onDislike(activityId) {
      await this.$axios.post(`/activities/activity_results/${activityId}/dislike/`).catch(() => {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
    },
    scrollToNextNotification() {
      if (this.notifications.length){
        this.$refs[this.scrollTargetId][0].$el.scrollIntoView()
        this.scrollTargetId = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.notifications {
  &__btn-wrapper {
    padding: 16px 0;
    position: sticky;
    bottom: 80px;
    display: flex;
    justify-content: flex-end;
  }
  &__btn {
    box-shadow: 1px 1px 6px 0 $grey05;
    border-radius: 4px;
    padding: 10px 14px;
  }
  &__icon{
    display: inline-block;
    vertical-align: middle;
  }
  &__item{
    border: solid 1px $grey10;
    border-radius: 8px;
    background-color: $white;
  }
  &__heading{
    font-size: $fs-nm;
    font-weight: bold;
    position: relative;
    padding-left: 16px;
    &:before{
      content: "";
      position: absolute;
      background: $grey16;
      top: 4px;
      left: 0;
      height: 12px;
      width: 12px;
      border-radius: 50%;
    }
    &--marked{
      &:before{
        background: $pink;
      }
    }
  }
  &__box{
    text-align: right;
  }
  &__count{
    font-size: $fs-m;
    color: $l-blue;
    font-weight: bold;
  }
  &__date{
    font-size: $fs-s;
  }
  &__body{
    border-top: solid 1px $grey10;
  }
  &__check {
    box-sizing: border-box;
    cursor: pointer;
    display: inline-block;
    position: relative;
    width: auto;
    &:before {
      background: #fff;
      border: 1px solid $grey05;
      content: '';
      display: block;
      height: 26px;
      margin-top: -10px;
      position: absolute;
      top: 50%;
      width: 26px;
      border-radius: 4px;
      right: 0;
    }
    &:after {
      border-right: 4px solid $l-blue;
      border-bottom: 4px solid $l-blue;
      content: '';
      display: block;
      height: 16px;
      margin-top: -7px;
      opacity: 0;
      position: absolute;
      top: 50%;
      transform: rotate(45deg);
      width: 10px;
      border-radius: 2px;
      right: 7px;
    }
  }
}

input[type="checkbox"]{
  display: none;
}

input[type=checkbox]:checked + .notifications__check::after {
    opacity: 1;
}

.comment{
  &__img{
    width: 30px;
    height: 30px;
    object-fit: cover;
  }
  &__name{
    font-weight: bold;
    font-size: $fs-m;
  }
  &__date{
    font-size: $fs-m;
  }
  &__body{
    color: $grey04;
  }
}

.notification-card-enter-active, .notification-card-leave-active {
  transition: opacity .2s;
}

.notification-card-enter, .notification-card-leave-to {
  opacity: 0;
}
</style>